
import React from 'react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { motion, AnimatePresence } from 'framer-motion';
import { NotificationItem, Notification } from './NotificationItem';
import { NotificationEmptyState } from './NotificationEmptyState';

interface NotificationListProps {
  loading: boolean;
  notifications: Notification[];
  markAsRead: (id: string) => void;
  deleteNotification: (id: string) => void;
  handleAcceptConnection: (connectionId: string, notificationId: string) => void;
  handleRejectConnection: (connectionId: string, notificationId: string) => void;
  handleAcceptRewardPledge?: (pledgeId: string, notificationId: string) => void;
  handleFulfillPledge?: (pledgeId: string, notificationId: string) => void;
  getNotificationDescription: (notification: Notification) => string;
  filter: 'all' | 'unread';
  onNavigate?: (path: string) => void;
  scrollClassName?: string;
}

export const NotificationList: React.FC<NotificationListProps> = ({
  loading,
  notifications,
  markAsRead,
  deleteNotification,
  handleAcceptConnection,
  handleRejectConnection,
  handleAcceptRewardPledge,
  handleFulfillPledge,
  getNotificationDescription,
  filter,
  onNavigate,
  scrollClassName = "h-[340px]",
}) => {
  const filteredNotifications = filter === 'unread' 
    ? notifications.filter(n => !n.is_read) 
    : notifications;

  if (loading) {
    return (
      <div className="p-8 text-center text-muted-foreground flex flex-col items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent border-primary animate-spin mb-2"></div>
        <p className="text-sm">Loading notifications...</p>
      </div>
    );
  }

  if (!filteredNotifications.length) {
    return <NotificationEmptyState />;
  }

  return (
    <ScrollArea className={scrollClassName}>
      <div className="py-1">
        <AnimatePresence initial={false}>
          {filteredNotifications.map((notification, index) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
              transition={{ duration: 0.2, delay: index * 0.03 }}
            >
              <NotificationItem
                notification={notification}
                markAsRead={markAsRead}
                deleteNotification={deleteNotification}
                handleAcceptConnection={handleAcceptConnection}
                handleRejectConnection={handleRejectConnection}
                handleAcceptRewardPledge={handleAcceptRewardPledge}
                handleFulfillPledge={handleFulfillPledge}
                getNotificationDescription={getNotificationDescription}
                onNavigate={onNavigate}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ScrollArea>
  );
};
